import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import Status from 'components/field/Status';
import StringInput from 'components/field/StringInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import ReasonConfigForm from 'pages/Settings/ReasonConfig/ReasonConfigForm';

import { ENUMS, ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

/**
 * @author Burak Ak
 */

const ChangeStatusForm = NiceModal.create(({ status, otoOrderIDs, orderIDs }) => {
	// Hooks and states declaration
	const modal = useModal();
	const axios = useAxiosPrivate();
	const dispatch = useDispatch();
	const date = new Date();
	const { showError, showSuccess } = useAlert();
	const defaultColSpan = {
		md: 6
	};
	const isLostOrDamaged = useMemo(() => status === 'lostOrDamaged', []);
	const isReturn = useMemo(() => status === 'returned' || status === 'reverseReturned', []);

	const [isLoading, setIsLoading] = useState(false);

	// Form & Schema setup
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					status: yup.string(),
					date: yup.date().required(),
					comments: yup.string().nullable(),
					returnReason: yup
						.string()
						.nullable()
						.when('status', {
							is: isReturn,
							then: (schema) => schema.required()
						})
				})
				.required(),
		[]
	);
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			status,
			date: date.toISOString(Date.now()),
			comments: null,
			returnReason: null
		}
	});

	const { handleSubmit, setValue, watch } = formMethods;

	const dateWatcher = watch('date');

	useEffect(() => {
		let orderNumbers = '';
		orderIDs.forEach((orderID, index) => {
			orderNumbers += orderID + (index !== orderIDs.length - 1 ? ', ' : '');
		});
		setValue('orderNumbers', orderNumbers);
	}, [orderIDs]);

	const onSubmit = (data) => {
		if (isReturn) {
			NiceModal.show(ConfirmationModal, {
				title: 'markAsReturned.confirmTitle',
				subTitle: 'markAsReturned.confirmContent',
				yesAction: async () => submitForm(data)
			});
		} else {
			submitForm(data);
		}
	};

	const submitForm = async (data) => {
		setIsLoading(true);
		try {
			const response = await axios.post(ORDER_ACTIONS.UPDATE_ORDER_STATUS, {
				otoOrderIDs,
				status: data.status,
				date: data.date,
				comments: data.comments,
				returnReason: data.returnReason || undefined
			});
			const successOrderIDs = [];
			const failedOrderIDs = [];
			let responseErrorMessage = '';

			response.data.forEach((order) => {
				if (order.success === true) {
					successOrderIDs.push(order.orderIds);
				}
				if (order.success === false) {
					failedOrderIDs.push(order.orderIds);
					responseErrorMessage += order.errorMsg;
				}
			});

			if (response.status === 200) {
				if (successOrderIDs.length > 0) {
					showSuccess({
						message: successOrderIDs.join(', ')
					});
				}

				if (failedOrderIDs.length > 0) {
					showError({
						message: (
							<Trans
								i18nKey={`errorMsg.${responseErrorMessage}`}
								values={{ failedOrders: failedOrderIDs.join(', ') }}
							/>
						),

						persist: true
					});
				}

				if (successOrderIDs.length === 0 && failedOrderIDs.length === 0) {
					showSuccess();
				}
				dispatch(updateDynamicListsInPage());
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
		setIsLoading(false);
		modal.remove();
	};

	const statusLabel = useMemo(() => {
		switch (status) {
			case 'returned':
			case 'reverseReturned':
				return {
					title: 'markAsReturned',
					date: 'returnDate'
				};
			case 'reverseHeldForPickup':
				return {
					title: 'markAsReverseHeldForPickup',
					date: 'modifiedDate'
				};
			case 'heldForPickup':
				return {
					title: 'markAsHeldForPickup',
					date: 'modifiedDate'
				};
			case 'undeliveredAttempt':
				return {
					title: 'markAsUndeliveredAttempt',
					date: 'modifiedDate'
				};
			case 'lostOrDamaged':
				return {
					title: 'markAsLostOrDamaged',
					date: 'modifiedDate'
				};
			default:
				return {
					title: 'markAsDelivered',
					date: 'deliveryDate'
				};
		}
	}, [status]);

	return (
		<Modal maxWidth="medium" fullwidth className="modal-changeStatus">
			<ModalTitle>
				<Typography variant="p1">{statusLabel.title}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="status" colSpan={defaultColSpan}>
						{isLostOrDamaged ? (
							<EnumSelect
								valueRemovable={false}
								searchable={false}
								value={watch('status')}
								setValue={(value) => {
									setValue('status', value, { shouldValidate: true, shouldDirty: true });
								}}
								enumName={ENUMS.LOST_OR_DAMAGED_STATUSES}
								labelPrefix="status."
								forcePrefix
							/>
						) : (
							<Status name="status" value={status} />
						)}
					</FormItem>

					<FormItem label="orderNumbers" colSpan={defaultColSpan}>
						<StringInput name="orderNumbers" disabled={true} />
					</FormItem>

					{!isLostOrDamaged && (
						<FormItem
							label={statusLabel.date}
							{...(isReturn && {
								colSpan: defaultColSpan
							})}
							required>
							<DateInput
								name="date"
								value={dateWatcher}
								setValue={(value) => {
									setValue('date', value, { shouldValidate: true, shouldDirty: true });
								}}
								maxDate={date.toISOString()}
							/>
						</FormItem>
					)}

					{isReturn && (
						<FormItem label="returnReason" required colSpan={defaultColSpan}>
							<EnumSelect
								name="returnReason"
								enumName={ENUMS.RETURN_REASON_CONFIG}
								placeholder="selectOne"
								value={watch('returnReason')}
								setValue={(value) => {
									setValue('returnReason', value, { shouldValidate: true, shouldDirty: true });
								}}
								firstItem={{
									action: () =>
										NiceModal.show(ReasonConfigForm, {
											setReasonConfig: (value) => {
												setValue(`returnReason`, value, {
													shouldValidate: true,
													shouldDirty: true
												});
											},
											reasonType: 'return'
										}),
									label: <Typography color="var(--blue)">addANewReason</Typography>
								}}
							/>
						</FormItem>
					)}

					{(isReturn || isLostOrDamaged) && (
						<FormItem label="ChangeStatusForm.comments">
							<TextArea name="comments" placeholder="" />
						</FormItem>
					)}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					onClick={() => {
						modal.remove();
					}}
					variant="outline"
				/>
				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					icon={faFloppyDisk}
					label="save"
					loading={isLoading}
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default ChangeStatusForm;
